import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Comment, SearchMember } from '../types/api';
import { getUserName } from '../services/auth';
import { Button } from './index';
import { X } from 'lucide-react';

interface CommentSectionProps {
  comments: Comment[];
  members: SearchMember[];
  onAddComment: (author: string, text: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  disabled?: boolean;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 0) {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CommentSection({
  comments,
  members: _members,
  onAddComment,
  onDeleteComment,
  disabled,
}: CommentSectionProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;

    const authorName = (await getUserName()) ?? 'Unknown';

    setSubmitting(true);
    try {
      await onAddComment(authorName, text.trim());
      setText('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="comment-section">
      <label className="input-label">{t('app.commentSection.label')}</label>

      <div className="comments-list">
        {comments.length === 0 && <p className="no-comments">{t('app.commentSection.noComments')}</p>}
        {comments.map((comment) => (
          <div key={comment.id} className="comment">
            <div className="comment-header">
              <span className="comment-author">{comment.author}</span>
              <div className="comment-header-right">
                <span className="comment-date">
                  {formatRelativeTime(comment.createdAt)}
                </span>
                <button
                  className="comment-delete-btn"
                  onClick={() => onDeleteComment(comment.id)}
                  disabled={disabled}
                  title={t('app.commentSection.deleteLabel')}
                  aria-label={t('app.commentSection.deleteLabel')}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
            <p className="comment-text">{comment.text}</p>
          </div>
        ))}
      </div>

      <form className="comment-form" onSubmit={handleSubmit}>
        <textarea
          className="comment-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('app.commentSection.placeholder')}
          disabled={disabled || submitting}
          rows={2}
        />
        <div className="comment-actions">
          <span />
          <Button type="submit" disabled={disabled || submitting || !text.trim()} size="sm">
            {submitting ? t('app.commentSection.adding') : t('app.commentSection.addButton')}
          </Button>
        </div>
      </form>
    </div>
  );
}
